import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { EmptyState } from '../components/EmptyState';
import { EngineIcon } from '../components/EngineIcon';
import { Icon } from '../components/Icon';
import { RefreshButton } from '../components/RefreshButton';
import { Pager, usePaged } from '../components/Pager';
import { useCrumb } from '../lib/crumb';
import { relTime } from '../lib/useFreshness';
import { useUrlNumber, useUrlState } from '../lib/useUrlState';
import { TargetEditForm } from './cluster-access/TargetEditForm';
import { Dialog } from '../components/Dialog';
import { engineForTarget, engineIndex, type EngineSource } from '../lib/targetEngine';
import {
  ATLAS_RECLAIMABLE_STATES,
  canPruneAtlas,
  type AtlasEntryState,
} from '@infras/shared-types';
import { useMe } from '../lib/useMe';
import {
  ATLAS_ENTRIES,
  ATLAS_PENDING,
  ATLAS_SUPPRESSED,
  DO_DATABASE,
  PROJECTS_OVERVIEW,
  PRUNE_ORPHANS,
  RESTORE_SUPPRESSED,
  REFRESH_LIVE_COUNT,
  STATE_LABEL,
  capacity,
  levelColor,
  minutesSince,
  stateTitles,
  type AtlasEntryRow,
  type AtlasProjectOverview,
} from '../lib/atlas';
import { canPruneKind, canSyncNow, KIND_LABEL, type TargetKind } from '../lib/targetKind';

/** A slot this page will offer to free. The list lives in shared-types so the row an
 *  operator can tick is exactly the row `pruneAtlasOrphans` will accept. */
const isReclaimable = (s: AtlasEntryState) => ATLAS_RECLAIMABLE_STATES.includes(s);

/**
 * `/atlas-whitelist/:projectKey` — one project, capacity first.
 *
 * The page it replaces had five equal-weight `<h3>` sections and two visually
 * identical IP tables — "live Atlas list" and "reported IPs" — separately filtered,
 * sorted and paged. The only question an operator actually arrives with is *what is
 * missing, what is extra, what is stale*, and answering it meant diffing two
 * paginated tables by eye. `Reconcile now` acted on exactly that diff and never
 * showed it.
 *
 * So: one capacity number at the top, then ONE table whose `State` column is the
 * diff, and the things that break quietly — the cap and agent heartbeat — given
 * their own space instead of being derivable.
 */

const RECONCILE = gql`
  mutation ReconcileAtlasWhitelist($projectKey: String) {
    reconcileAtlasWhitelist(projectKey: $projectKey) {
      status
      reason
      projects {
        projectKey
        status
        added
        removed
        skipped
        error
      }
    }
  }
`;

const TEST = gql`
  mutation TestAtlasConnection($projectKey: String!) {
    testAtlasConnection(projectKey: $projectKey) {
      success
      error
    }
  }
`;

const SYNC_RUNS = gql`
  query AtlasProjectSyncRuns($projectKey: String, $limit: Int) {
    atlasSyncRuns(projectKey: $projectKey, limit: $limit) {
      id
      status
      added
      removed
      skipped
      errorMessage
      triggeredBy
      startedAt
      listSizeAfter
    }
  }
`;

interface SyncRun {
  id: string;
  status: string;
  added: number;
  removed: number;
  skipped: number;
  errorMessage?: string | null;
  triggeredBy?: string | null;
  startedAt: string;
  listSizeAfter?: number | null;
}

interface PendingIp {
  id: string;
  ipCidr: string;
  clusterName?: string | null;
  nodeName?: string | null;
  lastSeenAt: string;
}

interface SuppressedIp {
  id: string;
  ipCidr: string;
  clusterName: string | null;
  nodeName: string | null;
  prunedAt: string;
  prunedBy: string | null;
  prunedReason: string | null;
}

type Tab = 'list' | 'pending' | 'history' | 'suppressed';

export function AtlasProjectPage() {
  const { projectKey = '' } = useParams();
  useCrumb(projectKey || null);

  const { role: pageRole } = useMe();
  const [tab, setTab] = useUrlState('tab');
  const activeTab: Tab =
    tab === 'pending' || tab === 'history' || tab === 'suppressed' ? tab : 'list';
  // Owned here rather than inside the table, because the capacity callout's
  // "Show N reclaimable entries" has to be able to set it — the whole point of
  // that button is to jump from the summary into the rows behind it.
  const [stateFilter, setStateFilter] = useUrlState('state', '', 'livepage');

  const overview = useQuery<{ atlasProjectsOverview: AtlasProjectOverview[] }>(PROJECTS_OVERVIEW, {
    fetchPolicy: 'cache-and-network',
  });
  const project = overview.data?.atlasProjectsOverview.find((p) => p.projectKey === projectKey);

  const entries = useQuery<{ atlasEntries: AtlasEntryRow[] }>(ATLAS_ENTRIES, {
    variables: { projectKey },
    skip: !projectKey || project?.hasCredentials === false,
    fetchPolicy: 'cache-and-network',
  });
  const pending = useQuery<{ atlasPendingIps: PendingIp[] }>(ATLAS_PENDING, {
    variables: { projectKey },
    skip: !projectKey,
    fetchPolicy: 'cache-and-network',
  });
  const suppressed = useQuery<{ atlasSuppressedIps: SuppressedIp[] }>(ATLAS_SUPPRESSED, {
    variables: { projectKey },
    skip: !projectKey,
    fetchPolicy: 'cache-and-network',
  });
  const runs = useQuery<{ atlasSyncRuns: SyncRun[] }>(SYNC_RUNS, {
    variables: { projectKey, limit: 15 },
    skip: !projectKey,
    fetchPolicy: 'cache-and-network',
  });
  // The engine behind the header icon (AC-4). ONE row by uuid, not the list the band
  // and the mappings page use: this drawer joins a single target, and the list resolver
  // pays a per-row firewall-rule count over the entire scanned DO estate to answer it.
  // Skipped entirely for an Atlas target — engineForTarget short-circuits on kind.
  const dbs = useQuery<{ doDatabase: EngineSource | null }>(DO_DATABASE, {
    variables: { uuid: project?.projectId ?? '' },
    skip: project?.kind !== 'do_db' || !project?.projectId,
    fetchPolicy: 'cache-and-network',
  });
  const engines = useMemo(() => engineIndex(dbs.data?.doDatabase ? [dbs.data.doDatabase] : []), [
    dbs.data,
  ]);
  const [editing, setEditing] = useState(false);
  // Reported up by the form so the dialog refuses Escape/Cancel mid-save.
  const [savingEdit, setSavingEdit] = useState(false);

  const refetchAll = () => {
    void overview.refetch();
    void entries.refetch();
    void pending.refetch();
    void runs.refetch();
    // A prune WRITES suppressed rows, and the "Removed by hand" tab only renders when
    // `suppressedRows` is non-empty — so without this the tab stays hidden until a manual
    // reload, on the one project where something was just removed. That tab is the only
    // per-address record there is (`atlas_whitelist_sync_runs` stores a count, not
    // addresses), so it has to be there the moment it has something to show.
    void suppressed.refetch();
  };

  const [reconcile, reconcileState] = useMutation(RECONCILE, { onCompleted: refetchAll });
  const [testConn, testState] = useMutation<{
    testAtlasConnection: { success: boolean; error?: string | null };
  }>(TEST);
  const [refresh, refreshState] = useMutation(REFRESH_LIVE_COUNT, { onCompleted: refetchAll });

  const rows = entries.data?.atlasEntries ?? [];
  const pendingRows = pending.data?.atlasPendingIps ?? [];
  const suppressedRows = suppressed.data?.atlasSuppressedIps ?? [];
  const runRows = runs.data?.atlasSyncRuns ?? [];
  // Root only, same gate as the prune: restoring re-arms a write to a third-party
  // allowlist on the next reconcile, so it belongs to whoever may remove.
  const canPrune = canPruneAtlas(pageRole ?? 'new_member');
  // Mirrors `@RequireRole('root','maintainer')` on updateAtlasProject (resolver :758).
  const canEdit = pageRole === 'root' || pageRole === 'maintainer';

  // Counts come from the ENTRIES we actually have, not the snapshot, so the tab
  // label and the table can never disagree about what is on screen.
  const stateCounts = useMemo(() => {
    const out = { in_sync: 0, no_agent: 0, stale: 0, manual: 0, open_to_all: 0 };
    for (const r of rows) out[r.state] += 1;
    return out;
  }, [rows]);
  const reclaimable = stateCounts.no_agent + stateCounts.stale;

  // Prefer the live queries when they have RESOLVED — they are this render's truth
  // — and fall back to the snapshot only while they haven't, so the numbers don't
  // flash to zero on load.
  //
  // The test is `data !== undefined`, never `length ||`: a project with genuinely
  // zero pending IPs would otherwise fall through to a stale snapshot and the
  // legend would say "Waiting to push 9" above a tab labelled "(0)". Two numbers
  // for the same thing on one screen is precisely the defect this page exists to
  // remove.
  const liveKnown = entries.data ? rows.length : (project?.liveCount ?? null);
  const pendingKnown = pending.data ? pendingRows.length : (project?.pendingCount ?? 0);
  // Kind decides the cap (200 entries vs 100 rules — e-10) and the vendor named in
  // every sentence. It no longer decides whether deletes exist: e-32 made a DO
  // revocation Atlas-parity, so both kinds get the same root-gated prune. Unknown until
  // the overview resolves — Atlas until then, which is what every row was before 052.
  const kind: TargetKind = project?.kind === 'do_db' ? 'do_db' : 'atlas';
  const isDo = kind === 'do_db';
  const vendor = isDo ? 'DigitalOcean' : 'Atlas';
  const cap = capacity(liveKnown, pendingKnown, kind);
  const color = levelColor(cap.level);
  const lastRun = runRows[0];
  // e-11: "Sync now" is the reconcile mutation, root|maintainer, and the only path that
  // writes to a DO firewall outside register (no scheduled sync in v1). For anyone else
  // the button is ABSENT on a do_db drawer, not disabled (AC-20).
  const syncAllowed = !isDo || canSyncNow(pageRole);

  if (!projectKey) return null;

  return (
    <div className="page atlas-project">
      {/* The back-link is gone: this page is a drawer over Cluster access now, and
          the screen it would go back to is behind it. The drawer's own Close and Esc
          are the way out. */}
      <div className="atlas-head">
        <div className="atlas-head__main">
          <div className="atlas-head__titles">
            {/* The engine, before the name: on a drawer opened from the DigitalOcean
                section the kind is already known, and WHICH database is the fact the
                header never carried (AC-4). */}
            <EngineIcon
              engine={engineForTarget(project, engines)}
              size={17}
            />
            <h2>{project?.displayName || projectKey}</h2>
            {project && (
              <>
                {isDo && (
                  <span className="badge quiet" data-testid="kind-badge">
                    {KIND_LABEL.do_db}
                  </span>
                )}
                <span className={`badge ${project.isActive ? 'confirmed' : 'quiet'}`}>
                  {project.isActive ? 'active' : 'inactive'}
                </span>
                <span className="badge quiet">
                  {project.hasCredentials ? 'credentials configured' : 'no credentials'}
                </span>
              </>
            )}
          </div>
          <div className="atlas-head__meta">
            <span className="mono">{projectKey}</span>
            {project?.projectId && (
              <>
                <span className="atlas-sep">·</span>
                <span className="mono" title={isDo ? 'DO database uuid' : 'Atlas project id'}>
                  {project.projectId}
                </span>
              </>
            )}
            {isDo && project?.doTeam && (
              <>
                <span className="atlas-sep">·</span>
                <span>
                  team <span className="mono">{project.doTeam}</span>
                </span>
              </>
            )}
            {project?.liveCountAt && (
              <>
                <span className="atlas-sep">·</span>
                <span>
                  read from {vendor} {relTime(Date.now() - new Date(project.liveCountAt).getTime())}{' '}
                  ago
                </span>
              </>
            )}
          </div>
        </div>

        <div className="atlas-head__actions">
          <div className="btn-row">
            {syncAllowed && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={!project?.hasCredentials || reconcileState.loading}
                onClick={() => void reconcile({ variables: { projectKey } })}
                title={
                  !project?.hasCredentials
                    ? isDo
                      ? 'Store a DO API token first'
                      : 'Configure OAuth credentials first'
                    : isDo
                      ? 'Add reported IPs that are missing from the DO firewall — add-only, never removes a rule'
                      : 'Add reported IPs that are missing from Atlas'
                }
              >
                {reconcileState.loading
                  ? isDo
                    ? 'Syncing…'
                    : 'Reconciling…'
                  : isDo
                    ? 'Sync now'
                    : 'Reconcile now'}
              </button>
            )}
            <button
              type="button"
              className="btn"
              disabled={!project?.hasCredentials || refreshState.loading}
              onClick={() => void refresh({ variables: { projectKey } })}
              title={`Re-read the live ${isDo ? 'firewall rules' : 'access list'} from ${vendor}`}
            >
              {refreshState.loading ? 'Reading…' : `Refresh from ${vendor}`}
            </button>
            {/* A do_db target is always testable (AC-28): without a token the server
                answers `No credentials for <key>` inline with zero HTTP (contract §5), so
                the button is not gated on credentials for that kind. Atlas keeps its gate. */}
            <button
              type="button"
              className="btn"
              disabled={(!isDo && !project?.hasCredentials) || testState.loading}
              onClick={() => void testConn({ variables: { projectKey } })}
            >
              {testState.loading ? 'Testing…' : 'Test connection'}
            </button>
            {/* Absent, not disabled, for anyone below maintainer: `updateAtlasProject`
                is @RequireRole('root','maintainer') and a control that exists only to
                return 403 is worse than no control. The api is still the enforcement. */}
            {canEdit && (
              <button type="button" className="btn" onClick={() => setEditing(true)}>
                Edit
              </button>
            )}
          </div>
          {lastRun && (
            <div className="atlas-head__note">
              Last {isDo ? 'sync' : 'reconcile'}{' '}
              {relTime(Date.now() - new Date(lastRun.startedAt).getTime())} ago — added{' '}
              {lastRun.added}, removed {lastRun.removed}
            </div>
          )}
          {testState.data && (
            <div className={testState.data.testAtlasConnection.success ? 'atlas-head__note' : 'error'}>
              {testState.data.testAtlasConnection.success
                ? 'Connection OK'
                : testState.data.testAtlasConnection.error || 'Connection failed'}
            </div>
          )}
        </div>
      </div>

      {/* Owner decision e-22: the edit form is a MODAL, like the add wizard — the two
          places a credential is typed behave the same way, and an inline form that
          unfolds half-way down a drawer is easy to leave open and lose. `Dialog`
          stops Escape and Tab in capture phase, so inside this Drawer one press
          closes the dialog and the drawer stays where it is; a second press closes
          the drawer. */}
      {canEdit && (
        <Dialog
          open={editing}
          onClose={() => setEditing(false)}
          title="Edit target"
          titleId="edit-target-title"
          size="md"
          busy={savingEdit}
        >
          <TargetEditForm
            projectKey={projectKey}
            kind={kind}
            onClose={() => setEditing(false)}
            onBusyChange={setSavingEdit}
            onSaved={() => overview.refetch().catch(() => undefined)}
          />
        </Dialog>
      )}

      <div className="atlas-grid">
        <div className="atlas-main">
          {/* ── Capacity ─────────────────────────────────────────────────── */}
          <section className="atlas-cap">
            <div className="atlas-cap__head">
              <span className="atlas-label">{isDo ? 'Firewall rule capacity' : 'Access list capacity'}</span>
              <span className="atlas-cap__limit">{vendor} hard limit</span>
            </div>

            <div className="atlas-cap__row">
              <span className="atlas-cap__n mono" style={{ color }}>
                {cap.known ? cap.live : '—'}
              </span>
              <span className="atlas-cap__of mono">/ {cap.cap}</span>
              <span className="atlas-cap__headline" style={{ color }}>
                {cap.known
                  ? `${cap.slotsLeft} slot${cap.slotsLeft === 1 ? '' : 's'} left`
                  : 'never read'}
              </span>
            </div>

            <div className="atlas-cap__bar">
              <span
                style={{ width: `${cap.livePct}%`, background: cap.known ? color : 'transparent' }}
              />
              <span className="atlas-cap__pend" style={{ width: `${cap.pendingPct}%` }} />
            </div>

            <div className="atlas-legend">
              <Legend label={`In ${vendor} now`} n={cap.known ? cap.live : null} color={color} />
              <Legend label="Waiting to push" n={cap.pending} color="var(--blue-soft-strong)" />
              <Legend label="Free after push" n={cap.known ? cap.free : null} color="var(--faint-deep)" />
            </div>

            {cap.known && (
              <div className={`atlas-note${cap.rejected ? ' atlas-note--crit' : ''}`}>
                <Icon name="alert" size={16} />
                <div>
                  <div className="atlas-note__title">
                    {cap.rejected
                      ? `The next ${isDo ? 'sync' : 'reconcile'} needs ${cap.projected} slots and will be rejected`
                      : `The next ${isDo ? 'sync' : 'reconcile'} will use ${cap.projected} of ${cap.cap} slots`}
                  </div>
                  <div className="atlas-note__body">
                    {cap.pending} reported IP{cap.pending === 1 ? '' : 's'} waiting to be pushed,
                    leaving {cap.free} free.{' '}
                    {/* Only claim something about reclaimability once the entry
                        table has actually loaded. Without credentials we never
                        read the list, and "nothing here is reclaimable" would be
                        an assertion about data we do not have. */}
                    {!entries.data ? (
                      <>The live list has not been read, so which entries are reclaimable is unknown.</>
                    ) : reclaimable > 0 ? (
                      <>
                        {reclaimable} entr{reclaimable === 1 ? 'y looks' : 'ies look'} reclaimable —{' '}
                        {stateCounts.no_agent} ha{stateCounts.no_agent === 1 ? 's' : 've'} no agent
                        behind {stateCounts.no_agent === 1 ? 'it' : 'them'} and {stateCounts.stale}{' '}
                        ha{stateCounts.stale === 1 ? 's' : 've'} not been seen recently.
                      </>
                    ) : (
                      <>Every entry has an agent reporting it — nothing here is reclaimable.</>
                    )}
                  </div>
                  {reclaimable > 0 && (
                    <button
                      type="button"
                      className="mini-btn"
                      onClick={() => {
                        setTab('');
                        setStateFilter('no_agent');
                      }}
                    >
                      Show {reclaimable} reclaimable entr{reclaimable === 1 ? 'y' : 'ies'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* ── Tabs ─────────────────────────────────────────────────────── */}
          <div className="tabs">
            {/* `—` rather than `(0)` when the query has not resolved: an unread
                list is unknown, and a zero in a tab label reads as a fact. */}
            <button className={activeTab === 'list' ? 'active' : ''} onClick={() => setTab('')}>
              Access list ({entries.data ? rows.length : '—'})
            </button>
            <button
              className={activeTab === 'pending' ? 'active' : ''}
              onClick={() => setTab('pending')}
            >
              Waiting to push ({pending.data ? pendingRows.length : '—'})
            </button>
            {/* Only appears once something has actually been suppressed — an always-on
                tab reading "(0)" is noise on the 20 projects nobody has ever pruned. */}
            {!!suppressedRows.length && (
              <button
                className={activeTab === 'suppressed' ? 'active' : ''}
                onClick={() => setTab('suppressed')}
              >
                Removed by hand ({suppressedRows.length})
              </button>
            )}
            <button
              className={activeTab === 'history' ? 'active' : ''}
              onClick={() => setTab('history')}
            >
              Sync history
            </button>
          </div>

          {activeTab === 'list' && (
            <EntryTable
              rows={rows}
              loading={entries.loading}
              error={!!entries.error}
              hasCredentials={project?.hasCredentials !== false}
              kind={kind}
              counts={stateCounts}
              state={stateFilter}
              onState={setStateFilter}
              projectKey={projectKey}
              // The capacity bar reads liveCount from `atlasProjectsOverview`, not
              // from the table — refetching only the entries would leave it stale.
              onPruned={refetchAll}
              onRefresh={() =>
                Promise.all([
                  entries.refetch().catch(() => undefined),
                  overview.refetch().catch(() => undefined),
                ])
              }
            />
          )}
          {activeTab === 'pending' && (
            <PendingTable
              rows={pendingRows}
              loading={pending.loading}
              onRefresh={() => pending.refetch().catch(() => undefined)}
            />
          )}
          {activeTab === 'suppressed' && (
            <SuppressedTable
              rows={suppressedRows}
              loading={suppressed.loading}
              canRestore={canPrune}
              projectKey={projectKey}
              onRestored={() => {
                void suppressed.refetch();
                void pending.refetch();
              }}
              onRefresh={() => suppressed.refetch().catch(() => undefined)}
            />
          )}
          {activeTab === 'history' && (
            <HistoryTable
              rows={runRows}
              kind={kind}
              onRefresh={() => runs.refetch().catch(() => undefined)}
            />
          )}
        </div>

      </div>
    </div>
  );
}

function Legend({ label, n, color }: { label: string; n: number | null; color: string }) {
  return (
    <span className="atlas-legend__item">
      <span className="atlas-swatch" style={{ background: color }} />
      {label}
      <span className="mono">{n ?? '—'}</span>
    </span>
  );
}

/**
 * The unified table — the fix for two-tables-no-diff.
 *
 * One row per entry Atlas actually returns, with a State column encoding whether
 * anything stands behind it. The footnote matters as much as the rows: people read
 * this as "what we intend to push" unless told otherwise.
 */
function EntryTable({
  rows,
  loading,
  error,
  hasCredentials,
  kind,
  counts,
  state,
  onState,
  projectKey,
  onPruned,
  onRefresh,
}: {
  rows: AtlasEntryRow[];
  loading: boolean;
  error: boolean;
  hasCredentials: boolean;
  /** Names the vendor in every string this table shows, and decides the cap. Both
   *  kinds prune (e-32): a DO firewall address is revoked the same way an Atlas one
   *  is, so the only per-kind difference below is the provider's name. */
  kind: TargetKind;
  counts: Record<string, number>;
  state: string;
  onState: (v: string) => void;
  projectKey: string;
  onPruned: () => void;
  onRefresh: () => unknown;
}) {
  const [q, setQ] = useUrlState('liveq', '', 'livepage');
  const [page, setPage] = useUrlNumber('livepage', 1);
  const { role } = useMe();
  // Root only — narrower than the page's other controls, which are root|maintainer.
  // A maintainer keeps the table and Reconcile; they just get no checkboxes. The kind
  // half of the gate is `canPruneKind`, true for both kinds since e-32; it stays in the
  // expression so a delete-less kind would remove these controls in one place.
  const canPrune = canPruneAtlas(role ?? 'new_member') && canPruneKind(kind);
  // The provider's name is the ONLY thing that differs between an Atlas prune and a DO
  // one — same gate, same preview, same three outcomes.
  const vendor = kind === 'do_db' ? 'DigitalOcean' : 'Atlas';

  // Selection is deliberately NOT url state: it is not shareable, and a link that
  // restored a selection would restore it against a list that has since moved.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  type Outcome = { cidr: string; reason: string };
  const [result, setResult] = useState<{
    removed: string[];
    refused: Outcome[];
    failed: Outcome[];
  } | null>(null);
  const [prune, pruneState] = useMutation<{
    pruneAtlasOrphans: { removed: string[]; refused: Outcome[]; failed: Outcome[] };
  }>(PRUNE_ORPHANS);

  const clearSelection = () => setSelected(new Set());
  /**
   * Every control that changes WHICH ROWS ARE ON SCREEN clears the selection first.
   * Without this a filter change leaves addresses ticked that the operator can no
   * longer see, and the confirm dialog is then the first place they reappear — which
   * is the worst possible moment to discover them.
   */
  const clearThen =
    <T,>(fn: (v: T) => void) =>
    (v: T) => {
      clearSelection();
      fn(v);
    };

  const needle = q.trim().toLowerCase();
  const filtered = rows.filter(
    (r) =>
      (!state || r.state === state) &&
      (!needle ||
        r.cidr.toLowerCase().includes(needle) ||
        (r.clusterName ?? '').toLowerCase().includes(needle) ||
        (r.nodeName ?? '').toLowerCase().includes(needle) ||
        (r.comment ?? '').toLowerCase().includes(needle)),
  );
  const pg = usePaged(filtered, 25, { page, onPage: setPage });

  // The refresh renders ABOVE the empty, error and not-configured branches, not only
  // inside the populated one: those are exactly the states somebody is waiting to see
  // change (a credential just added, Atlas just reachable again), and a control that
  // only exists once rows exist sends them back to F5 — the reload AC-11 removes. The
  // filters are NOT repeated here; there is nothing to narrow. Same shape as the
  // suppressed table below.
  // ONE control for this query (the count pin in check-cluster-access.mjs), reused in
  // both placements rather than written twice.
  const refreshControl = <RefreshButton refetch={onRefresh} />;
  const emptyToolbar = (
    <div className="filter-row" style={{ marginTop: 14 }}>
      <span className="spacer" />
      {refreshControl}
    </div>
  );

  if (!hasCredentials) {
    return (
      <>
        {emptyToolbar}
        <p className="muted" style={{ marginTop: 14 }}>
          This target has no {kind === 'do_db' ? 'DO API token' : 'OAuth credentials'} stored, so
          its live list has never been read. Add {kind === 'do_db' ? 'it' : 'them'} by editing the
          target.
        </p>
      </>
    );
  }
  if (error) {
    return (
      <>
        {emptyToolbar}
        <p className="error" style={{ marginTop: 14 }}>
          Couldn&apos;t reach Atlas for this project&apos;s access list.
        </p>
        <p className="muted">
          The <strong>Waiting to push</strong> tab still shows what agents report. Try{' '}
          <strong>Test connection</strong> — a rejected credential is the usual cause.
        </p>
      </>
    );
  }
  if (loading && !rows.length)
    return (
      <>
        {emptyToolbar}
        <p className="muted" style={{ marginTop: 14 }}>Loading…</p>
      </>
    );
  if (!rows.length) {
    return (
      <>
        {emptyToolbar}
        <EmptyState
          icon="shield"
          title="Atlas returned an empty access list"
          body="Nothing is whitelisted for this project yet. Map a cluster to it, then reconcile."
        />
      </>
    );
  }

  const FILTERS: { key: string; label: string }[] = [
    { key: '', label: 'All' },
    { key: 'in_sync', label: 'In sync' },
    { key: 'no_agent', label: 'No agent' },
    { key: 'stale', label: 'Stale' },
  ];

  // Select-all covers the reclaimable rows ON THIS PAGE only. There is deliberately
  // no "select all matching the filter" escalation: this deletes from a live
  // production allowlist, and every address should pass under someone's eyes.
  const pageSelectable = pg.pageItems.filter((r) => isReclaimable(r.state)).map((r) => r.cidr);
  // Across the whole FILTERED set, not just this page — the number the operator is
  // actually deciding about.
  const totalSelectable = filtered.filter((r) => isReclaimable(r.state)).length;
  const pageAllSelected =
    pageSelectable.length > 0 && pageSelectable.every((c) => selected.has(c));
  const pageSomeSelected = pageSelectable.some((c) => selected.has(c));

  const toggleRow = (cidr: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cidr)) next.delete(cidr);
      else next.add(cidr);
      return next;
    });
  const togglePage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (pageAllSelected) pageSelectable.forEach((c) => next.delete(c));
      else pageSelectable.forEach((c) => next.add(c));
      return next;
    });

  const chosen = rows.filter((r) => selected.has(r.cidr));

  const runPrune = async () => {
    const res = await prune({
      variables: { projectKey, confirmIps: [...selected] },
    });
    const out = res.data?.pruneAtlasOrphans;
    setResult({
      removed: out?.removed ?? [],
      refused: out?.refused ?? [],
      failed: out?.failed ?? [],
    });
    setConfirming(false);
    clearSelection();
    onPruned();
  };

  return (
    <>
      <div className="filter-row atlas-filters">
        <input
          type="search"
          placeholder="Filter by IP, cluster or comment…"
          value={q}
          onChange={(e) => clearThen(setQ)(e.target.value)}
        />
        <span className="atlas-seg">
          {FILTERS.map((f) => (
            <button
              key={f.key || 'all'}
              type="button"
              className={state === f.key ? 'on' : ''}
              onClick={() => clearThen(onState)(f.key)}
            >
              {f.label}
              {f.key ? ` ${counts[f.key] ?? 0}` : ''}
            </button>
          ))}
        </span>
        <span className="filter-count">
          {filtered.length} of {rows.length}
        </span>
        {/* Beside the filters, not above the card: the thing it refreshes is the rows
            these controls narrow, and the filter text survives the refresh (AC-11). */}
        {refreshControl}
      </div>

      {canPrune && selected.size > 0 && (
        <div className="atlas-prunebar">
          <strong>
            {selected.size} address{selected.size === 1 ? '' : 'es'} selected
          </strong>
          <span className="muted">
            {selected.size === 1 ? 'This slot' : 'These slots'} will be released on the live{' '}
            {kind === 'do_db' ? 'DO firewall' : 'Atlas list'}.
          </span>
          {/* Select-all covers THIS PAGE, and changing page clears the selection — so when
              more reclaimable rows exist further down, say so. Without this the operator
              reads "25 selected" as "all of them": on 2026-08-19 a prune of 36 reclaimable
              addresses removed the 25 on page 1, and the 11 on page 2 looked like a failure
              of the prune rather than a second page nobody was told about. */}
          {/* Measured against `pageSelectable.length`, NOT `selected.size`. Selection is
              always a subset of THIS page (every control that changes the visible rows runs
              `clearThen`), so `total - selected` also counts the rows sitting unticked right
              here — and calling those "on other pages" sends the operator hunting through a
              pager that may have only one page. `total - pageSelectable.length` is the
              off-page remainder and nothing else. */}
          {totalSelectable > pageSelectable.length && (
            <span className="muted">
              {totalSelectable - pageSelectable.length} more reclaimable{' '}
              {totalSelectable - pageSelectable.length === 1 ? 'address is' : 'addresses are'} on
              other pages — selection clears when you change page, so remove these first, then
              repeat.
            </span>
          )}
          <button
            type="button"
            className="mini-btn danger"
            disabled={pruneState.loading}
            onClick={() => setConfirming(true)}
          >
            Remove from {vendor}
          </button>
          <button type="button" className="mini-btn" onClick={clearSelection}>
            Cancel
          </button>
        </div>
      )}

      {pruneState.error && (
        <p className="error" style={{ marginTop: 10 }}>
          Removal failed: {pruneState.error.message}
        </p>
      )}

      <div className="atlas-card">
        <table className="atlas-table">
          <thead>
            <tr>
              {canPrune && (
                <th style={{ width: 28 }}>
                  <input
                    type="checkbox"
                    checked={pageAllSelected}
                    disabled={pageSelectable.length === 0}
                    ref={(el) => {
                      if (el) el.indeterminate = !pageAllSelected && pageSomeSelected;
                    }}
                    onChange={togglePage}
                    aria-label="Select every reclaimable address on this page"
                  />
                </th>
              )}
              <th>IP / CIDR</th>
              <th>Source</th>
              <th>{kind === 'do_db' ? 'Description in DO' : 'Comment in Atlas'}</th>
              <th>Last seen</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map((r) => {
              const mins = minutesSince(r.lastSeenAt);
              return (
                <tr key={r.cidr}>
                  {canPrune && (
                    <td style={{ width: 28 }}>
                      {/* Only reclaimable rows get a control. `manual` is somebody's
                          bastion and `open_to_all` is a finding, not prune fodder —
                          the server refuses both, so a checkbox would promise
                          something that cannot happen. */}
                      {isReclaimable(r.state) ? (
                        <input
                          type="checkbox"
                          checked={selected.has(r.cidr)}
                          onChange={() => toggleRow(r.cidr)}
                          aria-label={`Select ${r.cidr}`}
                        />
                      ) : null}
                    </td>
                  )}
                  <td className={`mono atlas-cidr${r.state === 'open_to_all' ? ' atlas-cidr--open' : ''}`}>
                    {r.cidr}
                  </td>
                  <td>
                    {r.clusterName || (r.state === 'open_to_all' ? 'open to the internet' : '—')}
                    {r.nodeName && <div className="atlas-sub mono">{r.nodeName}</div>}
                  </td>
                  <td className="mono atlas-comment">{r.comment || '—'}</td>
                  <td className={r.state === 'stale' ? 'atlas-stale' : 'muted'}>
                    {r.lastSeenAt && mins != null
                      ? `${relTime(mins * 60000)} ago`
                      : r.state === 'manual'
                        ? '—'
                        : 'never reported'}
                  </td>
                  <td>
                    <span className={`atlas-state atlas-state--${r.state}`} title={stateTitles(kind)[r.state]}>
                      {STATE_LABEL[r.state]}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager
        page={pg.page}
        pageCount={pg.pageCount}
        total={pg.total}
        start={pg.start}
        pageSize={pg.pageSize}
        onPage={clearThen(pg.setPage)}
      />
      <p className="atlas-foot muted">
        These are the records {kind === 'do_db' ? 'DigitalOcean' : 'Atlas'} itself returns for this
        target, not what we intend to push. A record with no agent behind it still occupies one of
        the {capacity(0, 0, kind).cap} slots.
        {kind === 'do_db' &&
          ' Non-IP rules (k8s, droplet, tag, app) are listed as manual and are never touched — they get no checkbox, and only the DO console can remove them.'}
      </p>

      {/* Hand-rolled rather than ConfirmDialog: its `body` is a plain string, and the
          one thing this dialog must do is show the operator each address it is about
          to delete. A count alone is not review. */}
      {confirming && (
        <div
          className="confirm-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !pruneState.loading) setConfirming(false);
          }}
        >
          <div
            className="confirm-card atlas-prune-card"
            role="dialog"
            aria-modal="true"
            aria-label={`Confirm removal from ${vendor}`}
          >
            <h3 className="confirm-title">
              Remove {chosen.length} address{chosen.length === 1 ? '' : 'es'} from {vendor}?
            </h3>
            <p className="confirm-body">
              This deletes {chosen.length === 1 ? 'it' : 'them'} from the live{' '}
              {kind === 'do_db' ? 'firewall rules' : 'access list'} for{' '}
              <strong>{projectKey}</strong>. Anything still behind{' '}
              {chosen.length === 1 ? 'this address' : 'these addresses'} loses database access. Each
              one is re-checked against {vendor} as it goes, and anything that has come back to life
              is refused rather than removed.
            </p>
            <ul className="atlas-prune-list">
              {chosen.map((r) => (
                <li key={r.cidr}>
                  <span className="mono">{r.cidr}</span>
                  <span className={`atlas-state atlas-state--${r.state}`}>
                    {STATE_LABEL[r.state]}
                  </span>
                  <span className="muted">{r.nodeName || r.clusterName || 'no source recorded'}</span>
                </li>
              ))}
            </ul>
            {/* "Keep them" rather than "Cancel": the action itself is a removal, and a
                dialog offering Cancel next to Remove makes the safe button ambiguous
                — the same trap ConfirmDialog's header warns about. */}
            <div className="btn-row confirm-actions">
              <button
                className="btn btn-danger"
                disabled={pruneState.loading}
                onClick={() => void runPrune()}
              >
                {pruneState.loading ? 'Removing…' : `Remove ${chosen.length}`}
              </button>
              <button
                className="btn btn-ghost"
                disabled={pruneState.loading}
                onClick={() => setConfirming(false)}
              >
                Keep them
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Three counts, never two. `refused` is our own verdict that an entry should
          stay; `failed` is a delete Atlas rejected and the operator must retry.
          Collapsing them would report an address as deliberately kept when the call
          actually broke — and a partial vendor failure mid-batch is the expected shape
          of a large prune, not the unlucky one. */}
      {result && (
        <div
          className={`atlas-prune-result${result.failed.length > 0 ? ' atlas-prune-result--failed' : ''}`}
        >
          <strong>
            {result.removed.length} removed
            {result.refused.length > 0 && ` · ${result.refused.length} refused`}
            {result.failed.length > 0 && ` · ${result.failed.length} failed`}
          </strong>
          {result.failed.length > 0 && (
            <details open>
              <summary>Failed at {vendor} — these are still whitelisted, retry them</summary>
              <ul>
                {result.failed.map((f) => (
                  <li key={f.cidr}>
                    <span className="mono">{f.cidr}</span> — {f.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {result.refused.length > 0 && (
            <details>
              <summary>Refused — kept on purpose</summary>
              <ul>
                {result.refused.map((f) => (
                  <li key={f.cidr}>
                    <span className="mono">{f.cidr}</span> — {f.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <button type="button" className="mini-btn" onClick={() => setResult(null)}>
            Dismiss
          </button>
        </div>
      )}
    </>
  );
}

/**
 * Addresses a human prune retired from the desired set.
 *
 * This screen exists because suppression deliberately removed the previous repair path.
 * Before it, reconcile re-added anything missing from Atlas, so a mistaken prune fixed
 * itself within 15 minutes. Now it does not — and the fleet will not fix it either, since
 * `atlasHealthy` is sticky-true and a healthy agent never re-registers. So the decision has
 * to be visible and reversible by a person, and `pruned_by` / `pruned_at` have to be on
 * screen: `atlas_whitelist_sync_runs` records only a COUNT of removals, never which
 * addresses, so this row is the only per-address evidence that exists.
 */
function SuppressedTable({
  rows,
  loading,
  canRestore,
  projectKey,
  onRestored,
  onRefresh,
}: {
  rows: SuppressedIp[];
  loading: boolean;
  canRestore: boolean;
  projectKey: string;
  onRestored: () => void;
  onRefresh: () => unknown;
}) {
  const [restore, state] = useMutation(RESTORE_SUPPRESSED);
  const [busy, setBusy] = useState<string | null>(null);

  // The toolbar renders ABOVE the empty check, not inside the populated branch: the
  // one tab where a person is WAITING for a row to appear is the tab that has none
  // yet, and a Refresh that only exists once rows exist sends them back to F5 —
  // the reload AC-11 exists to remove. Same placement as SchedulerJobPage:243.
  const toolbar = (
    <div className="filter-row" style={{ marginTop: 14 }}>
      <span className="filter-count">{rows.length} removed by hand</span>
      <span className="spacer" />
      <RefreshButton refetch={onRefresh} />
    </div>
  );

  if (loading && !rows.length)
    return (
      <>
        {toolbar}
        <p className="muted" style={{ marginTop: 14 }}>Loading…</p>
      </>
    );
  if (!rows.length) {
    return (
      <>
        {toolbar}
        <EmptyState
          icon="check"
          title="Nothing removed by hand"
          body="No address on this project has been pruned. Every reported IP is still desired."
        />
      </>
    );
  }

  const onRestore = async (ipCidr: string) => {
    setBusy(ipCidr);
    try {
      await restore({ variables: { projectKey, ipCidr } });
      onRestored();
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {state.error && (
        <p className="error" style={{ marginTop: 10 }}>
          Restore failed: {state.error.message}
        </p>
      )}
      {toolbar}
      <div className="atlas-card">
        <table className="atlas-table">
          <thead>
            <tr>
              <th>IP / CIDR</th>
              <th>Cluster</th>
              <th>Removed</th>
              <th>By</th>
              <th>State at removal</th>
              {canRestore && <th style={{ width: 90 }} />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="mono atlas-cidr">{r.ipCidr}</td>
                <td>{r.clusterName || '—'}</td>
                <td className="muted">
                  {relTime(Date.now() - new Date(r.prunedAt).getTime())} ago
                </td>
                <td className="atlas-sub">{r.prunedBy || '—'}</td>
                <td>
                  <span className="badge quiet">{r.prunedReason || 'pruned'}</span>
                </td>
                {canRestore && (
                  <td>
                    <button
                      type="button"
                      className="mini-btn"
                      disabled={busy === r.ipCidr}
                      onClick={() => void onRestore(r.ipCidr)}
                    >
                      {busy === r.ipCidr ? 'Restoring…' : 'Restore'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="atlas-foot muted">
        A prune removes the address from Atlas <em>and</em> retires the intent to push it, so
        reconcile will not add it back. Restoring returns it to the desired set — the next
        reconcile then re-adds it. An address a live agent reports again is un-suppressed
        automatically.
      </p>
    </>
  );
}

function PendingTable({
  rows,
  loading,
  onRefresh,
}: {
  rows: PendingIp[];
  loading: boolean;
  onRefresh: () => unknown;
}) {
  // Above the empty check on purpose — see SuppressedTable. This is the tab an
  // operator sits on while an agent reports a new IP; with no rows and no Refresh
  // the only way to see it arrive was a page reload.
  const toolbar = (
    <div className="filter-row" style={{ marginTop: 14 }}>
      <span className="filter-count">{rows.length} waiting</span>
      <span className="spacer" />
      <RefreshButton refetch={onRefresh} />
    </div>
  );

  if (loading && !rows.length)
    return (
      <>
        {toolbar}
        <p className="muted" style={{ marginTop: 14 }}>Loading…</p>
      </>
    );
  if (!rows.length) {
    return (
      <>
        {toolbar}
        <EmptyState
          icon="check"
          title="Nothing waiting to push"
          body="Every IP agents report for this project is already in the Atlas access list."
        />
      </>
    );
  }
  return (
    <>
      {toolbar}
      <div className="atlas-card">
        <table className="atlas-table">
          <thead>
            <tr>
              <th>IP / CIDR</th>
              <th>Cluster</th>
              <th>Node</th>
              <th>Reported</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="mono atlas-cidr">{r.ipCidr}</td>
                <td>{r.clusterName || '—'}</td>
                <td className="mono atlas-sub">{r.nodeName || '—'}</td>
                <td className="muted">
                  {relTime(Date.now() - new Date(r.lastSeenAt).getTime())} ago
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="atlas-foot muted">
        Agents reported these node IPs but they are not in the Atlas list yet. Until a reconcile
        pushes them, those pods stay <code>0/1</code> READY — readiness is gated on Atlas
        convergence.
      </p>
    </>
  );
}

/** Sync history, with the column that makes capacity a trend rather than a delta. */
function HistoryTable({
  rows,
  kind,
  onRefresh,
}: {
  rows: SyncRun[];
  kind: TargetKind;
  onRefresh: () => unknown;
}) {
  // Above the empty check — see SuppressedTable. A first sync run is exactly the
  // thing an operator waits on here after pressing Sync now / Reconcile.
  const toolbar = (
    <div className="filter-row" style={{ marginTop: 14 }}>
      <span className="filter-count">
        {rows.length ? `last ${rows.length} runs` : 'no runs'}
      </span>
      <span className="spacer" />
      <RefreshButton refetch={onRefresh} />
    </div>
  );

  if (!rows.length) {
    return (
      <>
        {toolbar}
        <EmptyState
          icon="clock"
          title="No sync runs yet"
          body={
            kind === 'do_db'
              ? 'Nothing has pushed to this firewall yet. An agent register or Sync now writes the first row.'
              : 'Reconcile has never run for this project. Press Reconcile now to push the reported IPs.'
          }
        />
      </>
    );
  }
  return (
    <>
      {toolbar}
      <div className="atlas-card">
      <table className="atlas-table">
        <thead>
          <tr>
            <th>Started</th>
            <th>Result</th>
            <th>Change</th>
            <th>List size after</th>
            <th>Triggered by</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const size = r.listSizeAfter;
            return (
              <tr key={r.id}>
                <td className="muted">{new Date(r.startedAt).toLocaleString()}</td>
                <td>
                  <span className={`badge ${r.status === 'ok' ? 'confirmed' : 'failed'}`}>
                    {r.status}
                  </span>
                  {r.errorMessage && <div className="error text-xs">{r.errorMessage}</div>}
                </td>
                <td className="mono">
                  +{r.added} −{r.removed} skip {r.skipped}
                </td>
                {/* NULL for every run recorded before the column existed — a gap is
                    honest, an invented number in a capacity trend is not. */}
                <td
                  className="mono"
                  style={{
                    color: size != null ? levelColor(capacity(size, 0, kind).level) : undefined,
                  }}
                >
                  {size ?? '—'}
                </td>
                <td className="muted">{r.triggeredBy || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </>
  );
}

